/**
 * "Switch user" (view-as) — the client-side session bookkeeping.
 *
 * A switch swaps the ACTIVE token (`xale_access_token`) for one the server
 * minted FOR the viewed person (it carries the real actor as the `act` claim),
 * and parks the manager's own token + user blob here under
 * `xale_actor_session` so "Switch back" is a local restore, no re-login.
 *
 * Dependency-free on purpose: `authSlice` calls the boot repair below at
 * module load, before the store exists.
 */
export const ACTOR_SESSION_KEY = "xale_actor_session";
export const ACCESS_TOKEN_KEY = "xale_access_token";
export const CURRENT_USER_KEY = "xale_current_user";
/** Tab-local stamp read by `useIdentityRouteGuard`; cleared on every swap. */
export const ROUTE_IDENTITY_KEY = "xale_route_identity";

export interface ActorSession {
  /** The manager's own access token, restored on switch back. */
  token: string;
  /** The `xale_current_user` blob exactly as it was when the switch began. */
  currentUserRaw: string | null;
  user: { id: number; name: string | null; email: string | null };
  viewing: { id: number; name: string; roleName: string | null };
  startedAt: string;
}

/** The server's `act` claim — who is really behind the active token. */
export interface ActClaim {
  userId: number;
  name: string | null;
  email: string;
  startedAt: string;
}

export function decodeJwtPayload(
  token: string | null | undefined,
): Record<string, unknown> | null {
  if (!token) return null;
  try {
    const part = token.split(".")[1];
    if (!part) return null;
    const base64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );
    const parsed = JSON.parse(json);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

export function isJwtExpired(token: string | null | undefined): boolean {
  const payload = decodeJwtPayload(token);
  const exp = Number(payload?.exp);
  if (!Number.isFinite(exp)) return true;
  return Date.now() >= exp * 1000;
}

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function readActorSession(): ActorSession | null {
  const raw = safeGet(ACTOR_SESSION_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<ActorSession>;
    if (!parsed || typeof parsed.token !== "string" || !parsed.user) return null;
    const id = Number(parsed.user.id);
    if (!Number.isFinite(id) || id <= 0) return null;
    return {
      token: parsed.token,
      currentUserRaw:
        typeof parsed.currentUserRaw === "string" ? parsed.currentUserRaw : null,
      user: {
        id,
        name: typeof parsed.user.name === "string" ? parsed.user.name : null,
        email: typeof parsed.user.email === "string" ? parsed.user.email : null,
      },
      viewing: {
        id: Number(parsed.viewing?.id) || 0,
        name: typeof parsed.viewing?.name === "string" ? parsed.viewing.name : "",
        roleName:
          typeof parsed.viewing?.roleName === "string"
            ? parsed.viewing.roleName
            : null,
      },
      startedAt:
        typeof parsed.startedAt === "string"
          ? parsed.startedAt
          : new Date(0).toISOString(),
    };
  } catch {
    return null;
  }
}

export function writeActorSession(session: ActorSession): void {
  localStorage.setItem(ACTOR_SESSION_KEY, JSON.stringify(session));
}

export function clearActorSession(): void {
  try {
    localStorage.removeItem(ACTOR_SESSION_KEY);
  } catch {
    /* storage disabled */
  }
}

/** The `act` claim on the ACTIVE token, if it is a view-as token. */
export function readActClaim(): ActClaim | null {
  const payload = decodeJwtPayload(safeGet(ACCESS_TOKEN_KEY));
  const act = payload?.act as Partial<ActClaim> | undefined;
  if (!act || typeof act !== "object") return null;
  const userId = Number(act.userId);
  if (!Number.isFinite(userId) || userId <= 0) return null;
  return {
    userId,
    name: typeof act.name === "string" ? act.name : null,
    email: typeof act.email === "string" ? act.email : "",
    startedAt:
      typeof act.startedAt === "string" ? act.startedAt : new Date(0).toISOString(),
  };
}

/**
 * True while the app runs on a borrowed session: the active token is a
 * view-as token (server truth, the `act` claim) AND the manager's own session
 * is parked locally, so a switch back is possible.
 */
export function isImpersonating(): boolean {
  return readActClaim() !== null && readActorSession() !== null;
}

/** Put the manager's own token + user blob back as the active session. */
export function restoreActorLocally(actor: ActorSession): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, actor.token);
  if (actor.currentUserRaw) {
    localStorage.setItem(CURRENT_USER_KEY, actor.currentUserRaw);
  } else {
    localStorage.removeItem(CURRENT_USER_KEY);
  }
  clearActorSession();
  try {
    sessionStorage.removeItem(ROUTE_IDENTITY_KEY);
  } catch {
    /* storage disabled */
  }
}

/**
 * Boot-time repair, run by `authSlice` before it reads the token: a view-as
 * token lives 8h; if it has expired while the manager's own token is still
 * good, put the manager back instead of bouncing them to /login for a session
 * they only borrowed. A parked session whose own token has ALSO expired is
 * dropped (the normal expiry wipe then applies).
 *
 * Returns true when the actor was restored.
 */
export function restoreActorIfViewTokenExpired(): boolean {
  const actor = readActorSession();
  if (!actor) return false;
  const active = safeGet(ACCESS_TOKEN_KEY);
  const activeIsViewAs = !!decodeJwtPayload(active)?.act;
  // The parked session only makes sense under a view-as token. Anything else
  // (a fresh login landed on top of it) is stale and must not resurrect.
  if (!activeIsViewAs) {
    clearActorSession();
    return false;
  }
  if (!isJwtExpired(active)) return false;
  if (isJwtExpired(actor.token)) {
    clearActorSession();
    return false;
  }
  restoreActorLocally(actor);
  return true;
}
